import { Box, Radio } from '@mui/material';
import type { KeyboardEvent } from 'react';

import type { SelectListItemProps } from '../types';

export const SelectListItem = ({
  selected,
  onSelect,
  children,
  'data-testid': dataTestId,
}: SelectListItemProps) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
      sx={(theme) => ({
        px: 1,
        py: 1.5,
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        cursor: 'pointer',
        // Flat rows separated by dividers (Figma) rather than bordered cards.
        borderBottom: `1px solid ${theme.palette.divider}`,
        // Figma shows selection via the filled radio only — rows stay white
        // (no persistent highlight band); a subtle hover tint is kept for UX.
        backgroundColor: 'transparent',
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      })}
    >
      {/* Presentational — selection is driven by the row click/keydown above.
          Medium (24px) radio to match Figma; row padding handles spacing. */}
      <Radio checked={selected} onChange={onSelect} tabIndex={-1} sx={{ p: 0 }} />
      {children}
    </Box>
  );
};
